import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtINR, fmtNum, fmtINRCompact } from "@/lib/format";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell, PieChart, Pie, AreaChart, Area, Legend,
} from "recharts";
import {
  Coins, Wallet, ArrowUpRight, ArrowDownRight, FileText, ShoppingBag, PackageOpen,
  AlertTriangle, Sparkles, TrendingUp, Settings2, Check,
} from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";
import { Repeat, ArrowRight } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import SetupChecklist from "@/components/SetupChecklist";
import OpenDocsWidget from "@/components/OpenDocsWidget";
import AdvanceDriftWidget from "@/components/AdvanceDriftWidget";
import SignupVendorPrompt from "@/components/SignupVendorPrompt";
import FeatureFlagOnboardingToast from "@/components/FeatureFlagOnboardingToast";
import AIInsights from "@/components/AIInsights";
import ApprovalInboxCard from "@/components/ApprovalInboxCard";
import FilingStatusStrip from "@/components/dashboard/FilingStatusStrip";
import VendorGstr3bHealthCard from "@/components/dashboard/VendorGstr3bHealthCard";
import TDSDashboardTile from "@/pages/TDSDashboardTile";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const PIE_COLORS = ["hsl(158 89% 33%)", "hsl(220 90% 55%)", "hsl(43 96% 44%)",
                    "hsl(22 96% 47%)", "hsl(280 76% 55%)", "hsl(198 93% 60%)"];

// Widget registry — each entry describes one dashboard card
const WIDGETS = [
  { id: "kpis",          label: "KPI row (Cash / AR / AP / Net Profit)", default: true },
  { id: "vendor_3b",     label: "Vendor GSTR-3B Health (at-risk ITC)",   default: true },
  { id: "filing_status", label: "GST filing status strip (1 / 2B / 3B)",  default: true },
  { id: "ai_inbox",      label: "AI Insights + Approval Inbox",            default: true },
  { id: "trend",         label: "Income vs Expense trend",                 default: true },
  { id: "gst",           label: "GST position card",                       default: true },
  { id: "ar_aging",      label: "AR Aging distribution",                   default: true },
  { id: "quick_actions", label: "Quick actions",                           default: true },
  { id: "top_customers", label: "Top customers (Pie)",                     default: true },
  { id: "top_products",  label: "Top products (Bar)",                      default: true },
  { id: "segment_ebitda",label: "Segment EBITDA (Bar)",                    default: true },
  { id: "cash_area",     label: "Cash runway (Area)",                      default: true },
];

const PREFS_KEY = "dashboard_widgets_v1";

const KPI = ({ label, value, sub, icon: Icon, tone = "default", testId }) => (
  <div className="card-sharp p-5 relative overflow-hidden" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="metric-value text-3xl mt-2">{value}</div>
        {sub && <div className={`text-xs mt-2 ${tone === "positive" ? "text-emerald-600" : tone === "negative" ? "text-red-600" : "text-muted-foreground"}`}>{sub}</div>}
      </div>
      <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${tone === "accent" ? "bg-accent/15 text-accent" : "bg-muted text-muted-foreground"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

/**
 * Dismissible kickoff banner that pushes new tenants to set up recurring
 * rent / SaaS / retainer invoices in <2 minutes. Auto-hides once at least
 * one recurring doc exists or the user X-es out.
 */
function RecurringKickoffTile() {
  const DISMISS_KEY = "recurring_kickoff_dismissed_v1";
  const [count, setCount] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  useEffect(() => {
    if (dismissed) return;
    api.get("/recurring").then(r => setCount(Array.isArray(r.data) ? r.data.length : 0))
       .catch(() => setCount(0));
  }, [dismissed]);
  if (dismissed || count === null || count > 0) return null;
  const hide = () => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };
  return (
    <div className="rounded-sm border border-accent/30 bg-gradient-to-r from-accent/10 via-accent/5 to-transparent p-4 md:p-5 relative" data-testid="recurring-kickoff-tile">
      <button onClick={hide}
                className="absolute top-2 right-2 text-muted-foreground/60 hover:text-foreground text-xs px-2 py-0.5 rounded-sm"
                aria-label="Dismiss" data-testid="recurring-kickoff-dismiss">×</button>
      <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
        <div className="w-11 h-11 rounded-sm bg-accent/20 flex items-center justify-center text-accent shrink-0">
          <Repeat className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// 90-second setup</div>
          <div className="font-heading font-semibold text-lg md:text-xl mt-0.5 leading-tight">
            Set up recurring rent / subscription invoices in 90 seconds
          </div>
          <div className="text-xs md:text-sm text-muted-foreground mt-1">
            Pick a customer, an amount, and a cadence. Zenith bills them automatically each month with the correct GST — you don't have to lift a finger on billing day.
          </div>
        </div>
        <Link to="/app/recurring" className="shrink-0" data-testid="recurring-kickoff-cta">
          <Button className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5 h-10 px-4">
            Set one up <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [aging, setAging] = useState({ buckets: {}, rows: [] });
  const [prods, setProds] = useState({ rows: [] });
  const [custs, setCusts] = useState({ rows: [] });
  const [seg, setSeg] = useState({ rows: [], columns: [] });
  const { openAI } = useOutletContext() || {};

  // Widget preferences from localStorage
  const defaultPrefs = useMemo(() => Object.fromEntries(WIDGETS.map((w) => [w.id, w.default])), []);
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      return raw ? { ...defaultPrefs, ...JSON.parse(raw) } : defaultPrefs;
    } catch { return defaultPrefs; }
  });
  const savePrefs = (next) => { setPrefs(next); localStorage.setItem(PREFS_KEY, JSON.stringify(next)); };
  const toggle = (id) => savePrefs({ ...prefs, [id]: !prefs[id] });
  const resetPrefs = () => savePrefs(defaultPrefs);

  useEffect(() => {
    api.get("/reports/dashboard").then((r) => setData(r.data));
    api.get("/reports/aging", { params: { party_type: "customer" } }).then((r) => setAging(r.data));
    const t = new Date(); const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
    const p = { from_date: `${y}-04-01`, to_date: new Date().toISOString().slice(0, 10) };
    api.get("/reports/mis/top-products", { params: p }).then((r) => setProds(r.data)).catch(() => {});
    api.get("/reports/mis/top-customers", { params: p }).then((r) => setCusts(r.data)).catch(() => {});
    api.get("/reports/mis/full-pnl", { params: { ...p, dimension: "business" } }).then((r) => setSeg(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 text-muted-foreground">Loading dashboard…</div>;
  const trend = data.trend?.length ? data.trend : [{ date: "—", income: 0, expense: 0 }];
  const topProdChart = (prods.rows || []).slice(0, 6).map((p) => ({ name: p.name.slice(0, 20), gm: p.gross_margin }));
  const topCustChart = (custs.rows || []).slice(0, 6).map((c) => ({ name: c.name.slice(0, 20), revenue: c.revenue }));
  const segEBITDA = (seg.columns || []).map((c) => {
    const row = seg.rows?.find((r) => r.key === "ebitda");
    return { name: c.label || c.name, ebitda: row?.values?.[c.id] || 0 };
  }).filter((r) => r.name !== "Total");
  const cashArea = trend.map((t) => ({ date: t.date, cash: (t.income || 0) - (t.expense || 0) }));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      <PageHeader eyebrow="Overview" title="Command centre"
        description="Live financial position, cash, GST liability, receivables and payables at a glance."
        actions={
          <div className="flex items-center gap-2">
            <button onClick={openAI} className="inline-flex items-center gap-2 h-9 px-3 border rounded-sm hover:bg-muted text-sm" data-testid="dashboard-open-ai">
              <Sparkles className="w-4 h-4 text-accent" /> Ask AI
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button className="inline-flex items-center gap-2 h-9 px-3 border rounded-sm hover:bg-muted text-sm" data-testid="dashboard-customize">
                  <Settings2 className="w-4 h-4" /> Customise
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72" align="end">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Show widgets</div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {WIDGETS.map((w) => (
                    <label key={w.id} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`widget-toggle-${w.id}`}>
                      <Checkbox checked={!!prefs[w.id]} onCheckedChange={() => toggle(w.id)} />
                      <span>{w.label}</span>
                    </label>
                  ))}
                </div>
                <div className="border-t mt-3 pt-2 flex justify-between">
                  <button onClick={resetPrefs} className="text-xs text-muted-foreground hover:text-foreground" data-testid="widget-reset">Reset defaults</button>
                  <span className="text-[10px] text-muted-foreground">Saved to this browser</span>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        } />

      {/* Feb 2026 · Post-signup one-time toast about hidden modules */}
      <FeatureFlagOnboardingToast />

      {/* Feb 2026 · First-run setup checklist — auto-hides once fully done */}
      <SetupChecklist />

      {/* Feb 2026 · Post-signup vendor prompt */}
      <SignupVendorPrompt />

      {/* Feb 2026 · Open workflow docs summary */}
      <OpenDocsWidget />

      {/* Feb 2026 · Advance Drift Watchdog — vendors with un-adjusted PO advances >60d */}
      <AdvanceDriftWidget />

      {prefs.filing_status && <FilingStatusStrip />}

      {/* Feb 2026 · Recurring Invoices Kickoff — dismissible banner */}
      <RecurringKickoffTile />

      {prefs.kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Cash Position" value={fmtINR(data.cash_position)} sub="Cash + Bank" icon={Wallet} tone="accent" testId="kpi-cash" />
          <Link to="/app/customers" className="block" data-testid="kpi-ar-link">
            <KPI label="Receivables (AR)" value={fmtINR(data.receivables)} sub={`${data.invoice_count} invoices · click to open`} icon={ArrowDownRight} testId="kpi-ar" />
          </Link>
          <Link to="/app/vendors" className="block" data-testid="kpi-ap-link">
            <KPI label="Payables (AP)" value={fmtINR(data.payables)} sub={`${data.bill_count} bills · click to open`} icon={ArrowUpRight} testId="kpi-ap" />
          </Link>
          <KPI label="Net Profit (YTD)" value={fmtINR(data.net_profit)} sub={data.net_profit >= 0 ? "Profit" : "Loss"} icon={TrendingUp} tone={data.net_profit >= 0 ? "positive" : "negative"} testId="kpi-np" />
        </div>
      )}

      {prefs.vendor_3b && data?.primary_gstin && (
        <VendorGstr3bHealthCard gstin={data.primary_gstin} />
      )}

      {prefs.ai_inbox && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2"><AIInsights /></div>
          <ApprovalInboxCard compact />
        </div>
      )}

      <TDSDashboardTile />

      {(prefs.trend || prefs.gst) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {prefs.trend && (
            <div className="card-sharp p-5 lg:col-span-2" data-testid="income-expense-trend">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Income vs Expense · last 14 days</div>
                  <div className="font-heading text-lg mt-1">Cashflow direction</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={fmtINRCompact} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  <Line type="monotone" dataKey="income" stroke="hsl(158 89% 33%)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="expense" stroke="hsl(22 96% 47%)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {prefs.gst && (
            <div className={`card-sharp p-5 ${!prefs.trend ? "lg:col-span-3" : ""}`} data-testid="gst-card">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">GST Position</div>
              <div className="metric-value text-3xl mt-2">{fmtINR(data.gst_liability)}</div>
              <div className="text-xs text-muted-foreground mt-1">Net GST payable / (input surplus)</div>
              <div className="border-t mt-4 pt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Income (YTD)</span><span className="tabular-nums">{fmtINR(data.income_total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Expense (YTD)</span><span className="tabular-nums">{fmtINR(data.expense_total)}</span></div>
                <div className="flex justify-between font-medium border-t pt-2"><span>Net Profit</span><span className="tabular-nums">{fmtINR(data.net_profit)}</span></div>
              </div>
            </div>
          )}
        </div>
      )}

      {(prefs.ar_aging || prefs.quick_actions) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {prefs.ar_aging && (
            <div className="card-sharp p-5" data-testid="aging-buckets">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">AR Aging</div>
              <div className="font-heading text-lg mt-1 mb-4">Receivables health</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={Object.entries(aging.buckets || {}).map(([k, v]) => ({ bucket: k, amount: v }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="bucket" fontSize={10} />
                  <YAxis fontSize={10} tickFormatter={fmtINRCompact} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  <Bar dataKey="amount">
                    {Object.keys(aging.buckets || {}).map((k, i) => (
                      <Cell key={k} fill={["hsl(158 89% 33%)", "hsl(43 96% 44%)", "hsl(22 96% 47%)", "hsl(0 84% 55%)"][i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {prefs.quick_actions && (
            <div className={`card-sharp p-5 ${!prefs.ar_aging ? "lg:col-span-3" : "lg:col-span-2"}`} data-testid="quick-links">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Quick actions</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Link to="/app/sales" className="border p-4 rounded-sm hover:bg-muted transition-colors">
                  <FileText className="w-4 h-4 text-accent mb-2" /><div className="text-sm font-medium">New Invoice</div>
                  <div className="text-xs text-muted-foreground mt-1">Bill a customer with GST</div>
                </Link>
                <Link to="/app/purchases" className="border p-4 rounded-sm hover:bg-muted transition-colors">
                  <ShoppingBag className="w-4 h-4 text-accent mb-2" /><div className="text-sm font-medium">New Bill</div>
                  <div className="text-xs text-muted-foreground mt-1">Book vendor purchase</div>
                </Link>
                <Link to="/app/vouchers" className="border p-4 rounded-sm hover:bg-muted transition-colors">
                  <Coins className="w-4 h-4 text-accent mb-2" /><div className="text-sm font-medium">Journal Voucher</div>
                  <div className="text-xs text-muted-foreground mt-1">Debit / credit entry</div>
                </Link>
                <Link to="/app/inventory" className="border p-4 rounded-sm hover:bg-muted transition-colors">
                  <PackageOpen className="w-4 h-4 text-accent mb-2" /><div className="text-sm font-medium">Stock</div>
                  <div className="text-xs text-muted-foreground mt-1">{data.low_stock_count > 0 ? `${data.low_stock_count} low-stock` : "All items in stock"}</div>
                  {data.low_stock_count > 0 && <AlertTriangle className="w-3.5 h-3.5 text-warning inline ml-1" />}
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {(prefs.top_customers || prefs.top_products) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {prefs.top_products && (
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Top products · gross margin</div>
              <div className="font-heading text-lg mt-1 mb-3">Winners</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={topProdChart} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" fontSize={10} tickFormatter={fmtINRCompact} />
                  <YAxis type="category" dataKey="name" fontSize={10} width={80} />
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Bar dataKey="gm" fill="hsl(158 89% 33%)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {prefs.top_customers && (
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Top customers · revenue</div>
              <div className="font-heading text-lg mt-1 mb-3">Revenue mix</div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={topCustChart} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name.slice(0, 10)}>
                    {topCustChart.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtINR(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {(prefs.segment_ebitda || prefs.cash_area) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {prefs.segment_ebitda && (
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Segment EBITDA — Business</div>
              <div className="font-heading text-lg mt-1 mb-3">By line of business</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={segEBITDA}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" fontSize={10} />
                  <YAxis fontSize={10} tickFormatter={fmtINRCompact} />
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Bar dataKey="ebitda" fill="hsl(280 76% 55%)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {prefs.cash_area && (
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Net cash · 14-day</div>
              <div className="font-heading text-lg mt-1 mb-3">Runway pulse</div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={cashArea}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" fontSize={10} />
                  <YAxis fontSize={10} tickFormatter={fmtINRCompact} />
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Area type="monotone" dataKey="cash" fill="hsl(220 90% 55% / 0.2)" stroke="hsl(220 90% 55%)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
